import * as React from "react";
import { Link } from "react-router-dom";
import { ReactComponent as IconEye } from "../../../../assets/svg/icon-eye.svg";
import { ReactComponent as IconEdit } from "../../../../assets/svg/icon-edit.svg";
import { ReactComponent as IconTrash } from "../../../../assets/svg/icon-trash.svg";
import { ReactComponent as IconFacebook } from "../../../../assets/svg/icon-facebook.svg";
import { ReactComponent as IconInstagram } from "../../../../assets/svg/icon-instagram.svg";
import { ReactComponent as OptionsIcon } from "../../../../assets/svg/options-icon.svg";
import { IconLinkedin, IconShare, IconLink } from "../../../../utils/icons";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../../../hooks/useUser";
import { isInternal, isProTeam } from "../../../../utils";
import { GroupAdd } from "@mui/icons-material";
import { IconUser } from "../../../../utils/icons";
import classNames from "classnames";
import { Checkbox, Divider, Tooltip } from "@mui/material";
import { getTextOverflowProps, KeyPointList } from "./helper";
import { CircularPercentage } from "../../createEditProfile/formFragments/CircularPercentage";
import { getFullProfilePercentage } from "../../createEditProfile/tabs";
import LinkIcon from '@mui/icons-material/Link';
import { cldUrl } from "../../../../utils/cloudinary";

export default function ProCard(props) {
  const { role } = useUser();
  const navigate = useNavigate();
  const {
    pro,
    deleteUser,
    isExpertPage,
    addToShortlist,
    isSharePath = false,
    onSelect,
    selected,
    createAutoLoginUrl,
  } = props;

  const [isChecked, setIsChecked] = React.useState(selected);
  const [showOptions, setShowOptions] = React.useState(false);

  const proDetailsPath = isSharePath
    ? `/experts/share?proId=${pro.userid}`
    : `/experts/${pro.userid}/${pro.slug || "-"}?proId=${pro.userid}`;

  const professionsList = React.useMemo(() => {
    const output = [];
    pro.professions?.forEach((set) => {
      if (set.subProfession) output.push(set.subProfession.name);
      else output.push(set.profession.name);
    });
    return output.slice(0, 4).join(" | ");
  }, [pro]);

  React.useEffect(() => {
    setIsChecked(selected);
  }, [selected]);

  const handleCheckboxChange = (event) => {
    const isChecked = event.target.checked;
    setIsChecked(isChecked);
    onSelect(pro.userid, isChecked);
  };

  return (
    <>
      <div
        className={classNames(
          "p-4 rounded-lg bg-surface col-span-1 flex flex-col justify-between min-w-[300px] border border-border-token relative",
          { "cursor-pointer hover:shadow-lg": isSharePath }
        )}
        onClick={() => isSharePath && navigate(proDetailsPath)}
      >
        <div className="flex flex-col gap-2">
          <div className="flex flex-row gap-2 sm:flex-row items-start relative">
            <div className="relative">
              {/* Selection only exists where the parent passes onSelect —
                  e.g. the expert profile page renders these cards without one,
                  and an unguarded checkbox would crash on click. */}
              {isInternal(role) && !isSharePath && typeof onSelect === "function" && (
                <Checkbox
                  checked={isChecked}
                  onChange={handleCheckboxChange}
                  sx={{
                    position: "absolute",
                    top: -15,
                    left: -15,
                    backgroundColor: "var(--surface) !important",
                    padding: "4px",
                    color: "var(--accent)",
                    "&.Mui-checked": {
                      color: "var(--accent)",
                    },
                  }}
                />
              )}

              {pro.avatar ? (
                <img
                  className="w-[100px] h-[100px] shrink-0 rounded object-cover object-top"
                  src={cldUrl(pro.avatar, { w: 200, h: 200, crop: "fill", gravity: "face" })}
                  alt={pro.displayName}
                />
              ) : (
                <div className="w-[100px] h-[100px] flex shrink-0 items-center justify-center rounded bg-tint">
                  <IconUser width={32} height={32} />
                </div>
              )}
            </div>

            <div className="flex flex-col gap-2 min-w-0 flex-1">
              <div className="text-lg text-text-1 font-bold cursor-pointer hover:text-accent-text truncate">
                {pro.displayName}
              </div>
              <div>
                <div>
                  {pro.categories?.map((category) => (
                    <div
                      key={category.id}
                      className="inline-block text-sm mb-1 mr-1 rounded-lg cursor-pointer text-white bg-accent"
                    >
                      <span className="block py-1 px-2">{category.name}</span>
                    </div>
                  ))}
                </div>
                {isInternal(role) && (
                  <div className="flex items-center">
                    <div className="flex items-center flex-wrap gap-1">
                      {!isSharePath && (
                        <div className="flex items-center justify-center">
                          <CircularPercentage
                            value={getFullProfilePercentage(pro)}
                            size={31}
                          />
                        </div>
                      )}
                      {!isSharePath && pro.facebook && (
                        <div className="bg-surface-2 w-8 h-8 rounded-full flex items-center justify-center">
                          <IconFacebook />
                        </div>
                      )}
                      {!isSharePath && pro.instagram && (
                        <div className="bg-surface-2 w-8 h-8 rounded-full flex items-center justify-center">
                          <IconInstagram />
                        </div>
                      )}
                      {!isSharePath && pro.linkedin && (
                        <div className="bg-surface-2 w-8 h-8 rounded-full flex items-center justify-center">
                          <IconLinkedin stroke="#A58450" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {!isSharePath && (
              <div
                className="absolute top-0 right-0 z-20"
                onMouseEnter={() => setShowOptions(true)}
                onMouseLeave={() => setShowOptions(false)}
              >
                <div className="cursor-pointer">
                  <OptionsIcon />
                </div>

                {showOptions && (
                  <div className="absolute p-2 bg-surface right-1 shadow-lg border border-border-token rounded-[10px] flex flex-col gap-1 z-30 min-w-[220px]">
                    
                    {isProTeam(role) && (
                      <Link
                        to={`/experts/${pro.userid}/${pro.slug || "-"
                          }/edit`}
                        className="!p-3 !px-4 flex gap-2 items-center cursor-pointer hover:bg-surface-2 hover:rounded-md"
                      >
                        <IconEdit width={24} height={24} size={24} stroke="#B0B0B0" />
                        <span style={{ margin: 0, fontFamily: 'CircularStd', fontWeight: 400, fontSize: '1rem', lineHeight: 1.5, color: 'var(--text-2)' }}>Edit</span>
                      </Link>
                    )}
                    {isInternal(role) && (
                      <div
                        onClick={() => createAutoLoginUrl(pro)}
                        className="!p-3 !px-4 flex gap-2 items-center cursor-pointer hover:bg-surface-2 hover:rounded-md"
                      >
                        <LinkIcon
                          width={24}
                          height={24}
                          sx={{ color: "var(--muted)" }}
                        />
                        <span style={{ margin: 0, fontFamily: 'CircularStd', fontWeight: 400, fontSize: '1rem', lineHeight: 1.5, color: 'var(--text-2)' }}>Auto Login URL</span>
                      </div>
                    )}
                    <a
                      href={proDetailsPath}
                      className="!p-3 !px-4 flex gap-2 items-center cursor-pointer hover:bg-surface-2 hover:rounded-md"
                    >
                      <IconEye width={24} height={24} size={24} stroke="#B0B0B0" />
                      <span style={{ margin: 0, fontFamily: 'CircularStd', fontWeight: 400, fontSize: '1rem', lineHeight: 1.5, color: 'var(--text-2)' }}>View</span>
                    </a>
                    {isProTeam(role) && (
                      <div
                        onClick={() => deleteUser(pro.userid)}
                        className="!p-3 !px-4 flex gap-2 items-center cursor-pointer hover:bg-surface-2 hover:rounded-md"
                      >
                        <IconTrash width={24} height={24} size={24} stroke="#B0B0B0" />
                        <span style={{ margin: 0, fontFamily: 'CircularStd', fontWeight: 400, fontSize: '1rem', lineHeight: 1.5, color: 'var(--text-2)' }}>Delete</span>
                      </div>
                    )}
                    {pro.status === "Active" && (
                      <a
                        href={`/experts/share?proId=${pro.userid}`}
                        className="!p-3 !px-4 flex gap-2 items-center cursor-pointer hover:bg-surface-2 hover:rounded-md"
                      >
                        <IconShare width={24} height={24} size={24} stroke="#B0B0B0" />
                        <span style={{ margin: 0, fontFamily: 'CircularStd', fontWeight: 400, fontSize: '1rem', lineHeight: 1.5, color: 'var(--text-2)' }}>Share URL</span>
                      </a>
                    )}
                    {isInternal(role) &&
                      addToShortlist &&
                      pro.status === "Active" &&
                      isExpertPage && (
                        <div
                          className="!p-3 !px-4 flex gap-2 items-center cursor-pointer hover:bg-surface-2 hover:rounded-md"
                          title="Add to Shortlist"
                          onClick={() => {
                            addToShortlist(pro);
                          }}
                        >
                          <GroupAdd
                            sx={{
                              height: "24px",
                              width: "24px",
                              color: "var(--muted)"
                            }}
                          />
                          <span style={{ margin: 0, fontFamily: 'CircularStd', fontWeight: 400, fontSize: '1rem', lineHeight: 1.5, color: 'var(--text-2)' }}>Add to Shortlist</span>
                        </div>
                      )}
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-[4px] whitespace-nowrap">
            {isInternal(role) && !isSharePath && (
              <div className="group flex items-center">
                <div className="p-1 border border-transparent rounded-md cursor-pointer bg-surface group-hover:bg-surface-2">
                  {pro.userid} |
                </div>
              </div>
            )}

            <Tooltip title={professionsList} arrow>
              <span
                className="text-text-2 font-[CircularStd] truncate"
                style={{
                  maxWidth: "100%",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  textOverflow: "ellipsis",
                  display: "inline-block",
                }}
              >
                {professionsList}
              </span>
            </Tooltip>
          </div>

          <Divider />
          <div className="text-text-2 text-sm font-[CircularStd]">
            {pro.description?.trim() || pro.keyPoints?.length > 0 ? (
              <>
                {pro.description?.trim() && (
                  <div
                    style={getTextOverflowProps(
                      pro.keyPoints?.length === 0 ? 6 : 2
                    )}
                  >
                    {pro.description}
                  </div>
                )}
                {pro.keyPoints?.length > 0 && (
                  <KeyPointList>
                    {pro.keyPoints?.slice(0, 4)?.map((point) => (
                      <li key={point.keyPoint} style={getTextOverflowProps(1)}>
                        {point.keyPoint}
                      </li>
                    ))}
                  </KeyPointList>
                )}
              </>
            ) : (
              <div className="text-muted">N/A</div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
